import { useCustomerIdToken } from '@hooks/customer/useCustomer';
import useSWRMutation from 'swr/mutation';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import {
  GraphQLRequestWithTokenProps,
  requestWithTokenRequired,
} from '@repo/ui/src/utils/graphql-request';
import { useSavedRecipe } from './useSavedRecipe';

interface Props {
  contentId: string;
}

interface Key extends GraphQLRequestWithTokenProps {
  contentId: string;
}

const saveRecipeQuery = `
  mutation SaveContent($input: SavedContentMutationInput!) {
    saveContent(input: $input) {
      contentId
    }
  }
`;

const unsaveRecipeQuery = `
  mutation UnsaveContent($input: SavedContentMutationInput!) {
    unsaveContent(input: $input) {
      contentId
    }
  }
`;

const fetcher = async ({ contentId, isSaved, ...requestProps }: Key & { isSaved: boolean }) => {
  if (!contentId) {
    throw new Error('Missing contentId for saving/unsaving recipe');
  }

  const query = isSaved ? unsaveRecipeQuery : saveRecipeQuery;

  const data = await requestWithTokenRequired({
    ...requestProps,
    document: query,
    variables: {
      input: {
        contentId,
        contentType: 'Recipe',
      },
    },
  });

  if (data?.errors) {
    throw new Error(data.errors[0].message);
  }

  return isSaved ? data?.unsaveContent : data?.saveContent;
};

export const useSaveRecipe = ({ contentId }: Props) => {
  const { locale } = useCurrentUrl();
  const idToken = useCustomerIdToken();
  const { isSaved, mutate } = useSavedRecipe({ slotId: contentId });

  return useSWRMutation(
    { document: saveRecipeQuery, contentId, locale, idToken, isSaved },
    async (key) => fetcher({ ...key, isSaved }),
    {
      onSuccess: () => {
        mutate();
      },
    },
  );
};
